import axios from 'axios';
import { API_CONFIG } from '../components/config/api';

export interface Language {
  code: string;
  name: string;
}

export const SUPPORTED_LANGUAGES: Language[] = [
  { code: 'en', name: 'English' },
  { code: 'de', name: 'Deutsch' },
  { code: 'fr', name: 'Français' },
  { code: 'es', name: 'Español' },
  { code: 'it', name: 'Italiano' },
];

// In-memory cache for translations
const translationCache: Record<string, Record<string, string>> = {};

export const loadTranslations = async (language: string): Promise<Record<string, string>> => {
  const langCode = language || 'en';

  if (translationCache[langCode]) {
    return translationCache[langCode];
  }

  try {
    const response = await fetchTranslationsFromApi(langCode);
    if (response) {
      translationCache[langCode] = response;
      return response;
    }
  } catch (error) {
    console.error(`Failed to fetch translations for ${langCode} from API:`, error);
  }

  return {};
};

const fetchTranslationsFromApi = async (language: string): Promise<Record<string, string> | null> => {
  try {
    const response = await axios.get(`${API_CONFIG.BASE_URL}/translations?language=${language}`);
    if (response.data && response.data.status === 'success') {
      return response.data.data;
    }
    return null;
  } catch (error) {
    console.error('Error fetching translations from API:', error);
    return null;
  }
};

export const getStoredLanguage = (): string => {
  return localStorage.getItem('destiny_language') || 'en';
};

export const setStoredLanguage = (language: string): void => {
  localStorage.setItem('destiny_language', language);
};

export const translate = (key: string, language: string, fallback?: string): string => {
  const translations = translationCache[language] || {};
  return translations[key] || fallback || key;
};

export const getLanguageName = (code: string): string => {
  const language = SUPPORTED_LANGUAGES.find(lang => lang.code === code);
  return language ? language.name : 'English';
};

export const setUserLanguagePreference = async (language: string): Promise<boolean> => {
  try {
    setStoredLanguage(language);

    if (!isAuthenticated()) {
      return true;
    }

    const response = await axios.post(`${API_CONFIG.BASE_URL}/user/language-preference`, {
      language
    });

    return response.data.status === 'success';
  } catch (error) {
    console.error('Error setting user language preference:', error);
    return false;
  }
};

const isAuthenticated = (): boolean => {
  return localStorage.getItem('destiny_auth') !== null;
};

export const addLanguageToUrl = (url: string): string => {
  const language = getStoredLanguage();
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}lang=${language}`;
};
